import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  VStack,
  Text,
  HStack,
  Icon,
  // Icon, // Ensure Chakra Icon is commented if not used directly
  useToast,
  Image,
  chakra,
  Tooltip,
} from '@chakra-ui/react';
import { FaCircle, FaMicrophone, FaStop } from 'react-icons/fa';
import { ElementType } from 'react';

interface RecorderProps {
  onChunkComplete: (chunk: Blob) => void;
  onSessionStart?: () => void;
  onSessionComplete?: () => void;
  isStartDisabled?: boolean;
  onShortRecordingStop?: () => void;
}

// Soundwave animation component
const SoundWave = chakra('span', {
  baseStyle: {
    display: 'inline-block',
    width: '24px',
    height: '24px',
    verticalAlign: 'middle',
    marginLeft: '8px',
  },
});

const AnimatedDot = chakra(Icon, {
  baseStyle: {
    animation: 'pulse 1.2s infinite',
    '@keyframes pulse': {
      '0%': { opacity: 1, transform: 'scale(1)' },
      '50%': { opacity: 0.5, transform: 'scale(1.3)' },
      '100%': { opacity: 1, transform: 'scale(1)' },
    },
  },
});

const Recorder: React.FC<RecorderProps> = ({ 
  onChunkComplete,
  onSessionStart,
  onSessionComplete,
  isStartDisabled = false,
  onShortRecordingStop
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0); // Total call duration
  // Use a ref for elapsed time to avoid stale closure issues
  const elapsedTimeRef = useRef(0);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number>();
  const currentMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const toast = useToast();
  const currentChunkDataRef = useRef<Blob[]>([]);
  const isChunkingRef = useRef(false); // Track if we've switched to chunking mode
  const shouldStartChunkingRef = useRef(false); // Flag to start chunking after single recorder stops
  const stopCalledRef = useRef(false); // Prevent double stop

  // Helper to get supported mimeType and extension
  const getSupportedMimeType = () => {
    if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
      return { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg' };
    } else {
      return { mimeType: 'audio/webm;codecs=opus', extension: 'webm' };
    }
  };

  // Start a new chunk recording (for chunking mode)
  const startNewChunk = async () => {
    const { mimeType } = getSupportedMimeType();
    const stream = streamRef.current;
    if (!stream) return;

    const mediaRecorder = new MediaRecorder(stream, { mimeType });
    currentMediaRecorderRef.current = mediaRecorder;
    currentChunkDataRef.current = [];
    stopCalledRef.current = false; // Reset flag for new recorder

    mediaRecorder.ondataavailable = (event) => {
      console.log('Recorder.tsx: ondataavailable fired', event.data.size, event.data.type);
      if (event.data.size > 0) {
        currentChunkDataRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      if (stopCalledRef.current) return; // Prevent double onstop logic
      stopCalledRef.current = true;
      if (currentChunkDataRef.current.length > 0) {
        const { extension } = getSupportedMimeType();
        const chunkBlob = new File(currentChunkDataRef.current, `chunk_${Date.now()}.${extension}`, { type: currentChunkDataRef.current[0].type });
        chunksRef.current.push(chunkBlob);
        onChunkComplete(chunkBlob);
      } else {
        console.warn('Recorder.tsx: No chunk data collected in currentChunkDataRef');
      }
    };
    mediaRecorder.start();
  };

  // Start a single MediaRecorder for short recordings
  const startSingleRecorder = async () => {
    const { mimeType } = getSupportedMimeType();
    const stream = streamRef.current;
    if (!stream) return;
    const mediaRecorder = new MediaRecorder(stream, { mimeType });
    currentMediaRecorderRef.current = mediaRecorder;
    currentChunkDataRef.current = [];
    stopCalledRef.current = false; // Reset flag for new recorder
    mediaRecorder.ondataavailable = (event) => {
      console.log('Recorder.tsx: ondataavailable fired (single)', event.data.size, event.data.type);
      if (event.data.size > 0) {
        currentChunkDataRef.current.push(event.data);
      }
    };
    mediaRecorder.onstop = () => {
      if (stopCalledRef.current) return; // Prevent double onstop logic
      stopCalledRef.current = true;
      if (currentChunkDataRef.current.length > 0) {
        const { extension } = getSupportedMimeType();
        const chunkBlob = new File(currentChunkDataRef.current, `chunk_${Date.now()}.${extension}`, { type: currentChunkDataRef.current[0].type });
        chunksRef.current.push(chunkBlob);
        onChunkComplete(chunkBlob);
      } else {
        console.warn('Recorder.tsx: No chunk data collected in currentChunkDataRef (single)');
      }
      
      // If we need to start chunking, do it now. This happens when the timer hits 60s.
      if (shouldStartChunkingRef.current) {
        shouldStartChunkingRef.current = false;
        startNewChunk();
      }
    };
    mediaRecorder.start();
  };

  const startRecording = async () => {
    onSessionStart?.();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      setIsRecording(true);
      setRecordingTime(0);
      elapsedTimeRef.current = 0;
      isChunkingRef.current = false;
      await startSingleRecorder();
      // Start timer for chunking
      timerRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
        elapsedTimeRef.current += 1;
        // Chunking logic (unchanged, invisible to user)
        if (!isChunkingRef.current && elapsedTimeRef.current >= 60) {
          isChunkingRef.current = true;
          shouldStartChunkingRef.current = true;
          if (currentMediaRecorderRef.current && currentMediaRecorderRef.current.state === 'recording') {
            currentMediaRecorderRef.current.stop();
          } else {
            startNewChunk();
          }
        } else if (isChunkingRef.current && currentMediaRecorderRef.current && currentMediaRecorderRef.current.state === 'recording') {
          if (elapsedTimeRef.current % 60 === 0) {
            currentMediaRecorderRef.current.stop();
            startNewChunk();
          }
        }
      }, 1000);
      toast({
        title: "Recording started",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast({
        title: "Error accessing microphone",
        description: "Please ensure you have granted microphone permissions",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const stopRecording = () => {
    console.log("Recorder.tsx: stopRecording called, isChunkingRef:", isChunkingRef.current, "isRecording:", isRecording);
    if (isRecording) {
      clearInterval(timerRef.current);
      setIsRecording(false);
      setRecordingTime(0);
  
      // If a recorder is active, stop it. Its onstop handler will process the final chunk.
      if (currentMediaRecorderRef.current && currentMediaRecorderRef.current.state === 'recording') {
        currentMediaRecorderRef.current.stop();
      }
  
      // Clean up stream resources.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
  
      // If this was a short recording (never entered chunking mode), mark it for finalization.
      if (!isChunkingRef.current) {
        onShortRecordingStop?.();
      }
  
      // Use a small timeout to allow the final `onstop` event to be processed before we finalize the session.
      setTimeout(() => {
        console.log("Recorder.tsx: stopRecording - Calling onSessionComplete to finalize session.");
        onSessionComplete?.();
      }, 250); // Delay allows final chunk to be added to the array in App.tsx before session is compiled.
  
      toast({
        title: "Recording stopped",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box w="100%" bgGradient="linear(to-br, blue.50, pink.50)" display="flex" flexDirection="column" alignItems="center" py={8}>
      <Box
        w="100%"
        maxW="400px"
        p={8}
        borderRadius="2xl"
        boxShadow="2xl"
        bg="white"
        textAlign="center"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <VStack gap={6} w="100%">
          <Text fontSize="2xl" fontWeight="bold" color="gray.800">
            {isRecording ? 'Recording in Progress' : 'Ready to Record'}
          </Text>
          {isRecording && (
            <HStack justifyContent="center" w="100%" spacing={3}>
              <AnimatedDot as={FaCircle as ElementType} color="red.500" boxSize={5} />
              <Text fontSize="lg" color="gray.700" fontWeight="semibold">
                {formatTime(recordingTime)}
              </Text>
              {/* Simple animated soundwave */}
              <SoundWave>
                <chakra.span
                  display="inline-block"
                  w="3px"
                  h="16px"
                  bg="red.400"
                  mx="1px"
                  borderRadius="full"
                  animation="wave1 1.2s infinite"
                  sx={{
                    '@keyframes wave1': {
                      '0%, 100%': { height: '8px' },
                      '50%': { height: '16px' },
                    },
                  }}
                />
                <chakra.span
                  display="inline-block"
                  w="3px"
                  h="12px"
                  bg="red.300"
                  mx="1px"
                  borderRadius="full"
                  animation="wave2 1.2s infinite"
                  style={{ animationDelay: '0.2s' }}
                  sx={{
                    '@keyframes wave2': {
                      '0%, 100%': { height: '6px' },
                      '50%': { height: '12px' },
                    },
                  }}
                />
                <chakra.span
                  display="inline-block"
                  w="3px"
                  h="18px"
                  bg="red.500"
                  mx="1px"
                  borderRadius="full"
                  animation="wave3 1.2s infinite"
                  style={{ animationDelay: '0.4s' }}
                  sx={{
                    '@keyframes wave3': {
                      '0%, 100%': { height: '10px' },
                      '50%': { height: '18px' },
                    },
                  }}
                />
              </SoundWave>
            </HStack>
          )}
          {!isRecording && (
            <Text fontSize="lg" color="gray.500" fontWeight="medium">
              Enter your name and email above, and press the button below to start recording
            </Text>
          )}
          {/* Large animated record/stop button */}
          <Tooltip
            label="Please enter your name and email above to start recording."
            isDisabled={!isStartDisabled || isRecording}
            hasArrow
            placement="top"
            openDelay={200}
          >
            <Button
              size="lg"
              borderRadius="full"
              boxSize="80px"
              bg={isRecording ? 'red.500' : 'blue.500'}
              color="white"
              fontSize="3xl"
              shadow="lg"
              _hover={{ bg: isRecording ? 'red.600' : 'blue.600', transform: 'scale(1.05)' }}
              _active={{ bg: isRecording ? 'red.700' : 'blue.700' }}
              onClick={isRecording ? stopRecording : startRecording}
              display="flex"
              alignItems="center"
              justifyContent="center"
              transition="all 0.2s"
              animation={isRecording ? 'pulseBtn 1.2s infinite' : undefined}
              sx={{
                '@keyframes pulseBtn': {
                  '0%': { boxShadow: '0 0 0 0 rgba(255,0,0,0.4)' },
                  '70%': { boxShadow: '0 0 0 16px rgba(255,0,0,0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(255,0,0,0)' },
                },
              }}
              disabled={isStartDisabled && !isRecording}
            >
              <Icon as={(isRecording ? FaStop : FaMicrophone) as ElementType} boxSize={10} />
            </Button>
          </Tooltip>
        </VStack>
      </Box>
    </Box>
  );
};

export default Recorder; 